/**
 * POSCoffeeClubRedemption - Shows Coffee Pass redemption status in POS
 * Displays if the customer has an active pass and can redeem their free coffee
 */
import { useState, useEffect } from 'react';
import { Coffee, Clock, CheckCircle, AlertCircle, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

export default function POSCoffeeClubRedemption({ 
  customer, 
  onAddFreeCoffee,
  compact = false 
}) {
  const [coffeeClubStatus, setCoffeeClubStatus] = useState(null);
  const [redemptionWindow, setRedemptionWindow] = useState(null);
  const [showUpgradeOption, setShowUpgradeOption] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch redemption window status
  useEffect(() => {
    const fetchWindow = async () => {
      try {
        const res = await api.get('/coffee-club/redemption-window');
        setRedemptionWindow(res.data);
      } catch (err) {
        console.error('Failed to fetch redemption window:', err);
      }
    };
    fetchWindow();
    
    // Refresh every minute
    const interval = setInterval(fetchWindow, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch customer's Coffee Pass status
  useEffect(() => {
    const fetchStatus = async () => {
      if (!customer?.email && !customer?.phone) {
        setCoffeeClubStatus(null);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (customer.email) params.append('email', customer.email);
        if (customer.phone) params.append('phone', customer.phone);
        
        const res = await api.get(`/coffee-club/status?${params}`);
        setCoffeeClubStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch coffee club status:', err);
        setCoffeeClubStatus(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatus();
  }, [customer?.email, customer?.phone]);

  // Don't show if loading, no customer, or no active pass
  if (loading || !customer || !coffeeClubStatus?.has_active_pass) {
    return null;
  }

  // Check if redemption window is open
  const windowOpen = redemptionWindow?.is_open;
  const canRedeem = coffeeClubStatus?.can_redeem && windowOpen;
  const alreadyRedeemed = coffeeClubStatus?.already_redeemed_today;

  const handleRedeemCoffee = async (upgradeTo12oz = false) => {
    if (!canRedeem) return;
    
    setRedeeming(true);
    try {
      const res = await api.post('/coffee-club/redeem', {
        pass_id: coffeeClubStatus.pass_id,
        channel: 'pos',
        is_standalone: true,
        upgrade_to_12oz: upgradeTo12oz
      });

      const data = res.data;
      
      // Add free coffee to cart
      onAddFreeCoffee({
        item_id: 'coffee-club-redemption',
        name: `Free ${upgradeTo12oz ? '12oz' : '8oz'} Coffee (Bula Club)`,
        price: upgradeTo12oz ? 3.00 : 0,
        quantity: 1,
        category: 'coffee',
        isCoffeeClubRedemption: true,
        upgradedTo12oz: upgradeTo12oz
      });
      
      toast.success(data.message || 'Free coffee added!');
      setShowUpgradeOption(false);
      
      // Update local status to reflect redemption
      setCoffeeClubStatus(prev => ({
        ...prev,
        already_redeemed_today: true,
        can_redeem: false
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem coffee');
    } finally {
      setRedeeming(false);
    }
  };

  if (compact) {
    // Compact version for sidebar
    return (
      <div 
        className="bg-amber-100 dark:bg-amber-900/30 rounded-xl p-3 border border-amber-200 dark:border-amber-800"
        data-testid="pos-coffee-club-compact"
      >
        <div className="flex items-center gap-2 mb-2">
          <Coffee className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Bula Coffee Pass</span>
        </div>
        
        {alreadyRedeemed ? (
          <p className="text-xs text-amber-600 dark:text-amber-400">Redeemed today</p>
        ) : !windowOpen ? (
          <p className="text-xs text-amber-600 dark:text-amber-400">Window: 7-10 AM or 1-3:30 PM</p>
        ) : canRedeem ? (
          <div className="space-y-2">
            {!showUpgradeOption ? (
              <Button
                size="sm"
                onClick={() => setShowUpgradeOption(true)}
                disabled={redeeming}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
              >
                <Gift className="w-3 h-3 mr-1" />
                Redeem Free Coffee
              </Button>
            ) : (
              <div className="grid grid-cols-2 gap-1">
                <Button
                  size="sm"
                  onClick={() => handleRedeemCoffee(false)}
                  disabled={redeeming}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                >
                  8oz FREE
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleRedeemCoffee(true)}
                  disabled={redeeming}
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8"
                >
                  12oz +3 FJD
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-amber-600 dark:text-amber-400">Cannot redeem</p>
        )}
      </div>
    );
  }

  // Full version
  return (
    <div 
      className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-4 border border-amber-200 dark:border-amber-800"
      data-testid="pos-coffee-club-redemption"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
          <Coffee className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="font-semibold text-amber-800 dark:text-amber-200">Bula Coffee Pass</h3>
          <p className="text-sm text-amber-600 dark:text-amber-400">{coffeeClubStatus.member_name}</p>
        </div>
      </div>

      {/* Status Message */}
      {alreadyRedeemed ? (
        <div className="flex items-center gap-2 p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <span className="text-sm text-gray-700 dark:text-gray-300">Already redeemed today</span>
        </div>
      ) : !windowOpen ? (
        <div className="flex items-center gap-2 p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl">
          <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">Redemption: 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM</span>
            <p className="text-xs text-gray-500">{redemptionWindow?.message}</p>
          </div>
        </div>
      ) : canRedeem ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 rounded-xl border border-green-200 dark:border-green-800">
            <Gift className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-sm text-green-700 dark:text-green-300">Free coffee ready to redeem!</span>
          </div>

          {!showUpgradeOption ? (
            <Button
              onClick={() => setShowUpgradeOption(true)}
              disabled={redeeming}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Redeem Free Coffee
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handleRedeemCoffee(false)}
                disabled={redeeming}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <span className="flex flex-col items-center py-1">
                  <span className="font-medium">8oz</span>
                  <span className="text-xs opacity-80">FREE</span>
                </span>
              </Button>
              <Button
                onClick={() => handleRedeemCoffee(true)}
                disabled={redeeming}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                <span className="flex flex-col items-center py-1">
                  <span className="font-medium">12oz</span>
                  <span className="text-xs opacity-80">+3 FJD</span>
                </span>
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl">
          <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-500">{coffeeClubStatus.window_message || 'Cannot redeem'}</span>
        </div>
      )}
    </div>
  );
}
